import i18n, { Module } from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import HttpBackend from 'i18next-http-backend';

// Utils
import getClientConfig from '../utils/getClientConfig';

const DEFAULT_LANGUAGE = 'en';

const config = getClientConfig();

// SimpleLocalize CDN configuration
const PROJECT_TOKEN = config?.simpleLocalize?.projectToken;
const CDN_BASE_URL = config?.simpleLocalize?.cdnBaseUrl;
const ENVIRONMENT = config?.simpleLocalize?.environment;

type LocaleModule = {
    default: Record<string, Record<string, unknown>>;
};

const localeLoaders: Record<string, () => Promise<LocaleModule>> = {
    en: () => import(/* webpackChunkName: "locales-en" */ '../../common/locales/en'),
};

const loadFallbackNamespace = async (namespace: string) => {
    const fallbackLoader = localeLoaders[DEFAULT_LANGUAGE];

    if (!fallbackLoader) {
        throw new Error(`Missing fallback i18n loader for language "${DEFAULT_LANGUAGE}".`);
    }

    const fallbackModule = await fallbackLoader();

    return fallbackModule.default?.[namespace];
};

const loadLocaleNamespace = async (language: string, namespace: string) => {
    const loader = localeLoaders[language] ?? localeLoaders[DEFAULT_LANGUAGE];

    if (!loader) {
        throw new Error(`Missing i18n loader for language "${language}" and fallback "${DEFAULT_LANGUAGE}".`);
    }

    const module = await loader();
    const resource = module.default?.[namespace];

    if (resource) return resource;

    if (language !== DEFAULT_LANGUAGE) {
        const fallbackResource = await loadFallbackNamespace(namespace);
        if (fallbackResource) return fallbackResource;
    }

    throw new Error(`Missing i18n namespace "${namespace}" for language "${language}".`);
};

// Common configuration shared between web and Electron
const commonConfig = {
    fallbackLng: DEFAULT_LANGUAGE,
    debug: process.env.NODE_ENV === 'development' ? true : false,
    load: 'languageOnly' as const,
    interpolation: {
        // Translations in this project use single-brace placeholders like {planName}
        prefix: '{',
        suffix: '}',
        // React already escapes values; keep false to allow HTML when intentionally used
        escapeValue: false,
    },
    react: {
        transSupportBasicHtmlNodes: false,
        useSuspense: false,
    },
    ns: [
        'checkout',
        'settings',
        'userInterface',
        'appHeader',
        'notifications',
        'common',
        'elements',
        'sharing',
        'toolbar',
        'popup',
        'canvas',
    ],

    // Return keys (not null/empty) when translations are missing, enabling parseMissingKeyHandler
    returnEmptyString: false,
    returnNull: false,
    saveMissing: false,

    // Return defaultValue when translation is missing, otherwise return the key
    parseMissingKeyHandler: (key: string, defaultValue?: string) => defaultValue || key,
};

// Track whether we're using CDN backend
let isUsingCdnBackend = false;

// Initialize i18n with local backend by default
i18n.use(initReactI18next).use(resourcesToBackend(loadLocaleNamespace)).init(commonConfig);

/**
 * Switch i18n backend to use CDN (SimpleLocalize)
 */
export const switchToCdnBackend = async (): Promise<void> => {
    if (isUsingCdnBackend) return;

    const buildCdnUrl = (languages: string[], namespaces: string[]) => {
        const lng = languages[0];
        const ns = namespaces[0];
        return `${CDN_BASE_URL}/${PROJECT_TOKEN}/${ENVIRONMENT}/${lng}/${ns}`;
    };

    // Add HttpBackend and initialize with backend configuration
    // If CDN fails, it will load fallback text
    await i18n.use(HttpBackend as unknown as Module).init({
        ...commonConfig,
        lng: i18n.language || DEFAULT_LANGUAGE,
        backend: {
            loadPath: buildCdnUrl,
        },
    });

    isUsingCdnBackend = true;
};

/**
 * Check if currently using CDN backend
 */
export const isUsingCdn = (): boolean => isUsingCdnBackend;

export default i18n;
